import { assert, isNormalizedResponse } from '@core/utils';
import { withTimeout } from 'es-toolkit';
import { permanentRedirect, redirect } from 'next/navigation';

import { ROUTES } from '@/constants/routes';

import { GPT_PROMPT_TYPE, UnauthApi } from '.';

export const getPolicyDetail = async (
  metaPolicyIdIdx: string,
  options?: { previewCode?: string },
) => {
  let redirectUrl: string | null = null;
  let isPermanentlyGone = false;
  const response = await UnauthApi[
    'get:/wello/v2/allows/policy/{metaPolicyIdIdx}'
  ]({
    path: {
      metaPolicyIdIdx: metaPolicyIdIdx,
    },
    query: {
      previewCode: options?.previewCode ? options.previewCode : undefined,
    },
  }).catch((e: unknown) => {
    if (isNormalizedResponse(e)) {
      const { return_code } = e;

      if (
        return_code === 24040 ||
        return_code === 20019 ||
        return_code === 24041
      ) {
        const toast =
          return_code === 24040
            ? '존재하지 않는 페이지입니다.'
            : return_code === 20019
              ? '더이상 제공하지 않는 페이지입니다.'
              : '숨김 처리된 페이지입니다.';
        redirectUrl = `${ROUTES.HOME.pathname}?toast=${encodeURIComponent(toast)}`;
        isPermanentlyGone = return_code === 24040;

        return null;
      }
    }

    throw e;
  });

  if (redirectUrl) {
    //* 숨김(20019·24041)은 재공개될 수 있어 308 캐시를 남기지 않는다
    if (isPermanentlyGone) permanentRedirect(redirectUrl);
    redirect(redirectUrl);
  }

  const data = response?.data;

  const context = data?.context;

  assert(context);

  let relatedInfo = '';
  if (context.applyDocuments)
    relatedInfo += `○ 구비서류\n - ${context.applyDocuments
      ?.replaceAll('\n', '\n  - ')
      .replaceAll('||', '\n  - ')}`;

  if (context.baseRule)
    relatedInfo += `${relatedInfo ? '\n' : ''}○ 관계법령\n - ${context.baseRule
      ?.replaceAll('\n', '\n  - ')
      .replaceAll('||', '\n  - ')}`;

  if (!relatedInfo) relatedInfo = '- 관련 정보 없음';

  const [summaryString, keywordString] = await withTimeout(async () => {
    let summaryString = context.aiSummary;
    let keywordString = context.aiSeoKeyword;

    [summaryString, keywordString] = await Promise.all([
      summaryString
        ? summaryString
        : UnauthApi['put:/wello/v2/allows/policy/{metaPolicyIdIdx}/ai']({
            path: {
              metaPolicyIdIdx: metaPolicyIdIdx,
            },
            body: {
              gptPromptType: GPT_PROMPT_TYPE.SUMMARY,
            },
          }).then(({ data }) => data?.context?.result),
      keywordString
        ? keywordString
        : UnauthApi['put:/wello/v2/allows/policy/{metaPolicyIdIdx}/ai']({
            path: {
              metaPolicyIdIdx: metaPolicyIdIdx,
            },
            body: {
              gptPromptType: GPT_PROMPT_TYPE.KEYWORD,
            },
          }).then(({ data }) => data?.context?.result),
    ]);

    return [summaryString, keywordString];
  }, 3_000).catch(() => []);

  const aiSummaryList = summaryString
    ?.split('***')
    .map((s) => s.trim())
    .slice(1);

  const aiKeywordList = keywordString
    ?.split(' | ')
    .map((s) => s.trim())
    .filter((keyword) => !/[\u4E00-\u9FFF]/.test(keyword));

  const faqList =
    context.faqList?.flatMap((faq) =>
      faq.question && faq.answer
        ? [
            {
              question: faq.question,
              answer: faq.answer,
            },
          ]
        : [],
    ) ?? [];

  return {
    id: context.id,
    idx: context.metaPolicyIdIdx,
    policyName: context.policyName,
    descAgeList: context.descAgeList,
    dday: context.dday,
    policyApplyId: context.welloPolicyApplyId,
    provisionTypeList: context.provisionTypeList,
    applyTypeList: context.applyTypeList,
    agencyLogo: context.agencyLogo,
    agency: context.agency,
    dueDateBetween: context.dueDateBetween,
    supportBenefit: context.supportBenefit,
    descTarget: context.descTarget,
    descSupport: context.descSupport,
    descProvision: context.descProvision,
    relatedInfo,
    policyBannerTypeList: context.policyBannerTypeList,
    descApply: context.descApply,
    agencyTel: context.agencyTel,
    infoUrl: context.infoUrl,
    aiSummaryList,
    aiKeywordList,
    policyApplicationTypeCd: context.policyApplicationTypeCd,
    detailPolicyApplicationTypeCd: context.detailPolicyApplicationTypeCd,
    htmlContents: context.htmlContents,
    policyCardContents: context.policyCardContents,
    faqList,
  };
};

export type PolicyDetail = Awaited<ReturnType<typeof getPolicyDetail>>;
